import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '@/components/Navbar'
import { ReviewListItem } from '@/components/ReviewListItem'
import apiService from '@/api/service'
import type { CommentDetail } from '@/types/api'

interface GeneratedReply {
    id: number
    text: string
}

export const ReviewsCenter = (): JSX.Element => {
    const navigate = useNavigate()
    const [comments, setComments] = useState<CommentDetail[]>([])
    const [selectedComment, setSelectedComment] = useState<CommentDetail | null>(null)
    const [loading, setLoading] = useState(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const [error, setError] = useState<string>('')
    const [generatedReplies, setGeneratedReplies] = useState<GeneratedReply[]>([])
    const [selectedReplyId, setSelectedReplyId] = useState<number | null>(null)

    useEffect(() => {
        // 使用 AbortController 來處理組件卸載時的清理
        const abortController = new AbortController()
        let isMounted = true

        const fetchComments = async () => {
            try {
                setLoading(true)
                setError('')

                // 使用真實 API 抓取資料
                const storeCode = apiService.getCurrentStoreCode()

                if (!storeCode) {
                    console.warn('未找到 store_code，跳轉到登入頁面')
                    navigate('/login')
                    return
                }

                console.log('正在從 API 獲取評論列表...')
                const response = await apiService.getComments({
                    store_code: storeCode,
                    comments_start: 1,
                    comments_quota: 50  // 獲取 50 筆評論
                })

                // 檢查組件是否已卸載
                if (!isMounted || abortController.signal.aborted) {
                    console.log('組件已卸載，取消數據更新')
                    return
                }

                console.log('評論列表獲取成功:', response)

                // 檢查是否為 token 失效錯誤
                if (response && response.status === 'failed' && 
                    response.message && response.message.includes('This tokens is invalid, please login again')) {
                    console.warn('Token 已失效，清除登入資料並跳轉到登入頁面')
                    
                    // 清除所有登入相關資料
                    apiService.logout()
                    
                    // 跳轉到登入頁面
                    navigate('/login')
                    return
                }

                if (response.status === 'success' && response.data) {
                    setComments(response.data)
                    // 預設選中第一筆評論
                    if (response.data.length > 0) {
                        setSelectedComment(response.data[0])
                    }
                } else {
                    throw new Error(response.message || '獲取評論失敗')
                }
            } catch (error) {
                // 檢查是否是因為組件卸載而中止
                if (!isMounted || abortController.signal.aborted) {
                    console.log('請求已中止')
                    return
                }

                console.error('Failed to fetch comments:', error)
                const errorMessage = error instanceof Error ? error.message : '載入評論失敗'
                setError(errorMessage)
            } finally {
                if (isMounted && !abortController.signal.aborted) {
                    setLoading(false)
                }
            }
        }

        fetchComments()

        // 清理函數：組件卸載時執行
        return () => {
            console.log('ReviewsCenter 組件卸載，清理資源')
            isMounted = false
            abortController.abort()
        }
    }, [navigate])

    const handleBack = () => {
        navigate('/dashboard')
    }

    const handleGenerateReply = async () => {
        if (!selectedComment) return

        console.log('=== 開始生成 AI 回覆 ===')
        console.log('評論資料:', selectedComment)
        setIsGenerating(true)

        try {
            const storeCode = apiService.getCurrentStoreCode()
            console.log('店家代碼:', storeCode)

            if (!storeCode) {
                console.error('未找到店家代碼')
                alert('未找到店家代碼，請重新登入')
                setIsGenerating(false)
                return
            }

            const requestParams = {
                store_code: storeCode,
                caption: selectedComment.caption,
                id_review: selectedComment.id_review || `comment_${Date.now()}`
            }

            console.log('=== API 請求參數 ===')
            console.log('store_code:', requestParams.store_code)
            console.log('caption:', requestParams.caption)
            console.log('id_review:', requestParams.id_review)
            console.log('完整請求參數:', requestParams)

            console.log('正在調用 replyComments API...')
            const response = await apiService.replyComments(requestParams)

            console.log('=== API 響應結果 ===')
            console.log('響應狀態:', response.status)
            console.log('響應訊息:', response.message)
            console.log('響應時間戳:', response.timestamp)
            console.log('回覆數量:', response.data?.length || 0)
            console.log('完整響應:', response)

            // 檢查是否為 token 失效錯誤
            if (response && response.status === 'failed' && 
                response.message && response.message.includes('This tokens is invalid, please login again')) {
                console.warn('Token 已失效，清除登入資料並跳轉到登入頁面')
                
                // 清除所有登入相關資料
                apiService.logout()
                
                // 跳轉到登入頁面
                navigate('/login')
                return
            }

            console.log('=== 處理 API 響應 ===')
            console.log('原始回覆數據:', response.data)

            const apiReplies: GeneratedReply[] = response.data.map((text, index) => ({
                id: index + 1,
                text: text
            }))

            console.log('轉換後的回覆格式:', apiReplies)
            console.log('回覆數量:', apiReplies.length)

            setGeneratedReplies(apiReplies)
            setSelectedReplyId(1)

            console.log('=== AI 回覆生成完成 ===')
        } catch (error) {
            console.error('=== API 調用失敗 ===')
            console.error('錯誤類型:', error instanceof Error ? error.constructor.name : typeof error)
            console.error('錯誤訊息:', error instanceof Error ? error.message : String(error))
            console.error('完整錯誤:', error)
            alert('生成回覆失敗，請重試')
        } finally {
            setIsGenerating(false)
            console.log('=== 生成流程結束 ===')
        }
    }

    const handleCopyReply = async () => {
        const selectedReply = generatedReplies.find(r => r.id === selectedReplyId)
        if (!selectedReply) {
            alert('請先選擇一個回覆')
            return
        }
        try {
            await navigator.clipboard.writeText(selectedReply.text)
            alert('回覆已複製到剪貼板！')
        } catch (error) {
            console.error('複製失敗:', error)
            alert('複製失敗，請重試')
        }
    }

    const handleSelectReply = (id: number) => {
        setSelectedReplyId(id)
    }

    // 當選擇新的評論時，重置 AI 回覆狀態
    const handleCommentSelect = (comment: CommentDetail) => {
        console.log('選擇新評論，重置 AI 回覆狀態')
        setSelectedComment(comment)
        // 重置 AI 回覆相關狀態
        setGeneratedReplies([])
        setSelectedReplyId(null)
    }

    // API 返回的是相對時間（如"1 週前"），直接顯示即可
    const formatRelativeTime = (relativeTime: string): string => {
        return relativeTime
    }

    const selectedRating = selectedComment ? parseFloat(selectedComment.rating) : 0

    return (
        <div className="bg-white w-full min-h-screen flex flex-col">
            <Navbar />

            <div className="w-full flex flex-col md:flex-row bg-[#eaeade] flex-1 overflow-hidden">
                {/* 左側：評論列表 - 响应式 */}
                <div className="flex-1 min-w-0 md:min-w-[350px] lg:min-w-[400px] h-[300px] md:h-full relative overflow-hidden flex flex-col">
                    {/* 列表標題 - 响应式 */}
                    <div className="absolute top-0 left-0 w-full h-[90px] md:h-[114px] bg-[#eaeade] z-10">
                        <div className="absolute top-0 left-3 md:left-5 w-full h-[50px] md:h-[60px] flex gap-2 md:gap-[9px] items-center">
                            {/* 返回按鈕 - 响应式 */}
                            <button
                                onClick={handleBack}
                                className="w-[50px] h-[50px] md:w-[60px] md:h-[60px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <div className="h-8 w-8 md:h-10 md:w-10 relative">
                                    <img
                                        className="absolute w-[25.00%] h-[50.00%] top-[23.23%] left-[21.46%]"
                                        alt="Vector"
                                        src="/img/vector-10.svg"
                                    />
                                    <img
                                        className="absolute w-[25.00%] h-[50.00%] top-[23.23%] left-[46.46%]"
                                        alt="Vector"
                                        src="/img/vector-10.svg"
                                    />
                                </div>
                            </button>

                            <div className="h-[26px] md:h-[30px] text-[#595959] text-lg md:text-[24.7px] tracking-[1.80px] md:tracking-[2.47px] flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                回覆中心
                            </div>
                        </div>
                    </div>

                    {/* 評論列表（可滾動）- 响应式 */}
                    <div className="flex flex-col w-full items-start pt-[90px] md:pt-[114px] overflow-y-auto h-full">
                        {loading ? (
                            <div className="w-full text-center py-10 text-gray-500">載入中...</div>
                        ) : error ? (
                            <div className="w-full text-center py-10 text-red-500">{error}</div>
                        ) : comments.length > 0 ? (
                            comments.map((comment, index) => (
                                <ReviewListItem
                                    key={index}
                                    comment={comment}
                                    isSelected={selectedComment === comment}
                                    onClick={() => handleCommentSelect(comment)}
                                />
                            ))
                        ) : (
                            <div className="w-full text-center py-10 text-gray-500">暫無評論資料</div>
                        )}
                    </div>

                    {/* 滾動條 - 响应式，小屏幕隐藏 */}
                    <div className="hidden md:block absolute top-[90px] md:top-[114px] right-0 w-4 h-[calc(100%-90px)] md:h-[calc(100%-114px)] bg-[#b3b3b3] overflow-hidden">
                        <div className="mt-[11.4px] w-[15.9px] h-[348.96px] bg-[#818181] rounded-[87.46px]" />
                    </div>
                </div>

                {/* 右側：AI 回覆建議區域 - 响应式 */}
                <div className="w-full md:max-w-[600px] lg:max-w-[756px] h-full relative bg-white overflow-hidden flex flex-col">
                    {/* 標題區 - 响应式 */}
                    <div className="w-full h-[70px] md:h-[95px] flex items-center justify-center bg-[#aeceb7] shadow-[0px_3.53px_8.83px_#00000040]">
                        <div className="[font-family:'Noto_Sans_TC',Helvetica] font-bold text-white text-lg md:text-[24.7px] tracking-[1.80px] md:tracking-[2.47px] leading-[normal]">
                            AI 評論回覆建議
                        </div>
                    </div>

                    {selectedComment ? (
                        <>
                            {/* 選中的評論詳情 - 响应式 */}
                            <div className="w-full px-3 md:px-4 py-3 md:py-4 border-b border-gray-200">
                                <div className="flex items-center gap-3 md:gap-4 mb-2">
                                    {/* 用戶頭像 - 响应式 */}
                                    <div className="w-[50px] h-[50px] md:w-[66px] md:h-[66px] flex items-center justify-center flex-shrink-0">
                                        <div className="h-[36px] w-[36px] md:h-[44.17px] md:w-[44.17px] bg-[#d9d9d9] rounded-[18px] md:rounded-[22.09px]" />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        {/* 用戶名稱 - 响应式 */}
                                        <div className="text-[#1a1a1a] text-xs md:text-[14.1px] tracking-[1.00px] md:tracking-[1.41px] [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal] mb-1.5 md:mb-2 truncate">
                                            {selectedComment.username}
                                        </div>

                                        {/* 星級和時間 - 响应式 */}
                                        <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                                            {/* 星級 - 响应式 */}
                                            <div className="flex items-center gap-1 md:gap-[6px]">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <img
                                                        key={star}
                                                        className="w-[12px] h-[11px] md:w-[15px] md:h-3.5"
                                                        alt="Star"
                                                        src={star <= selectedRating ? "/img/star-3-8.svg" : "/img/star-4-8.svg"}
                                                    />
                                                ))}
                                            </div>

                                            {/* 時間 - 响应式 */}
                                            <div className="text-[#999898] text-[9px] md:text-[10.6px] tracking-[0] [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                                {formatRelativeTime(selectedComment.relative_date)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 評論內容 - 响应式 */}
                                <div className="text-black text-sm md:text-base tracking-[1.20px] md:tracking-[1.60px] [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-relaxed md:leading-[normal] mt-3 md:mt-4">
                                    {selectedComment.caption}
                                </div>
                            </div>

                            {/* AI 回覆生成區域 - 响应式 */}
                            <div className="flex-1 flex items-center justify-center px-3 md:px-4 py-4 md:py-8">
                                <div className="w-full max-w-[650px] h-[400px] md:h-[525px] relative rounded-[15px] md:rounded-[17.67px] overflow-hidden border-[0.88px] border-solid border-[#939393] bg-white">
                                    <div className="absolute top-3 md:top-[13px] left-4 md:left-[21px] h-[18px] md:h-[21px] text-black text-sm md:text-[17.7px] tracking-[1.40px] md:tracking-[1.77px] whitespace-nowrap flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                        AI生成回覆內容
                                    </div>

                                    {/* 如果還沒生成回覆，顯示生成按鈕 - 响应式 */}
                                    {generatedReplies.length === 0 ? (
                                        <button
                                            onClick={handleGenerateReply}
                                            disabled={isGenerating}
                                            className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[160px] md:w-[200px] h-[45px] md:h-[50px] flex items-center justify-center bg-[#7da387] rounded-[8px] md:rounded-[10px] overflow-hidden hover:bg-[#6d9377] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="h-5 md:h-6 w-auto px-3 md:px-4 text-white text-base md:text-xl tracking-[2.40px] md:tracking-[4.00px] whitespace-nowrap flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                                {isGenerating ? '生成中...' : '點擊生成回覆'}
                                            </div>
                                        </button>
                                    ) : (
                                        /* 顯示生成的回覆列表 */
                                        <div className="absolute top-[50px] left-0 right-0 bottom-0 overflow-y-auto px-5 pb-20">
                                            <div className="flex flex-col gap-3">
                                                {generatedReplies.map((reply) => (
                                                    <div
                                                        key={reply.id}
                                                        onClick={() => handleSelectReply(reply.id)}
                                                        className={`relative flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors ${selectedReplyId === reply.id
                                                            ? 'bg-[#e8e8dc]'
                                                            : 'bg-white hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {/* Checkbox */}
                                                        <div className="flex-shrink-0 mt-1">
                                                            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${selectedReplyId === reply.id
                                                                ? 'bg-[#7da387] border-[#7da387]'
                                                                : 'bg-[#d9d9d9] border-[#d9d9d9]'
                                                                }`}>
                                                                {selectedReplyId === reply.id && (
                                                                    <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path d="M5 13l4 4L19 7"></path>
                                                                    </svg>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* 回覆內容 */}
                                                        <div className="flex-1">
                                                            <div className="text-black text-sm tracking-[1.40px] leading-[1.6] [font-family:'Noto_Sans_TC',Helvetica] font-bold mb-1">
                                                                生成回覆 {reply.id}
                                                            </div>
                                                            <div className="text-black text-sm tracking-[1.40px] leading-[1.8] [font-family:'Noto_Sans_TC',Helvetica] font-normal">
                                                                {reply.text}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* 複製回覆按鈕 */}
                                            <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                                                <button
                                                    onClick={handleCopyReply}
                                                    disabled={selectedReplyId === null}
                                                    className="w-[140px] h-[45px] flex items-center justify-center bg-[#7da387] rounded-[10px] overflow-hidden hover:bg-[#6d9377] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                                >
                                                    <div className="text-white text-base tracking-[1.60px] whitespace-nowrap flex items-center justify-center [font-family:'Noto_Sans_TC',Helvetica] font-bold leading-[normal]">
                                                        複製回覆
                                                    </div>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400 text-xl">
                            請從左側列表選擇一則評論
                        </div>
                    )}

                    {/* 滾動條 */}
                    <div className="absolute top-[95px] right-0 w-4 h-[calc(100%-95px)] bg-[#b3b3b3] overflow-hidden">
                        <div className="mt-[12.0px] w-[15.9px] h-[723.88px] bg-[#818181] rounded-[87.46px]" />
                    </div>
                </div>
            </div>
        </div>
    )
}


